import React, { useState } from 'react';
import { useAppStore } from '../../../../../../GlobalStore/GlobalStore.ts';
import { MdAdd } from 'react-icons/md';
import InfoButton from '../../Subcomponents/InfoButton/InfoButton.tsx';
import { writeSettings } from '../../../../../../Utils/db/users/userSettings/writeSettings.ts';
import { CustomMode, CustomModesSectionProps } from './types';
import { PremadeMode } from './premadeModes';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import EditableMode from './EditableMode';
import CreateModeForm from './CreateModeForm';
import ModeListItem from './ModeListItem';
import PremadeModeSuggestions from './PremadeModeSuggestions';

const CustomModesSection: React.FC<CustomModesSectionProps> = ({ customModes, onCustomModesChange }) => {
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setSettingsObj = useAppStore((state: any) => state.setSettingsObj);
    const userObj = useAppStore((state: any) => state.userObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { mode } = settingsObj;

    const [editingMode, setEditingMode] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [deletingMode, setDeletingMode] = useState<string | null>(null);
    const [selectedPremadeMode, setSelectedPremadeMode] = useState<PremadeMode | null>(null);

    // Helper function to check for duplicate names (case-insensitive comparison)
    const isNameDuplicate = (name: string, excludeName?: string) => {
        const trimmedName = name.trim().toLowerCase();
        if (trimmedName === '') return false;

        // Check if any existing mode key matches (case-insensitive)
        const existingKeys = Object.keys(customModes).map(key => key.toLowerCase());
        const excludeKey = excludeName?.trim().toLowerCase();

        return existingKeys.some(key => key === trimmedName && key !== excludeKey);
    };

    const handleCreateMode = (modeName: string, mode: CustomMode) => {
        // Use the mode name as the key (preserve exact case)
        const modeKey = modeName.trim();

        // Use callback to update parent component state
        const updatedModes = { ...customModes, [modeKey]: mode };
        onCustomModesChange(updatedModes);
        setIsCreating(false);
        setSelectedPremadeMode(null);
    };

    const handleDeleteMode = async (modeKey: string) => {
        const { [modeKey]: deleted, ...remainingModes } = customModes;

        // If we're deleting the currently selected mode, switch to agent mode
        if (mode === modeKey) {
            setSettingsObj({ mode: 'agent' });
            // Write the mode change to the database immediately since this affects current chat
            if (userObj.user) {
                await writeSettings(userObj.user, { mode: 'agent' }, environmentObj.environment);
            }
        }

        // Use callback to update parent component state
        onCustomModesChange(remainingModes);
        setDeletingMode(null);
    };

    const handleEditMode = (oldModeKey: string, newModeName: string, updatedMode: CustomMode) => {
        const newModeKey = newModeName.trim();
        let updatedModes = { ...customModes };

        // If name changed (key changed), remove old entry and add new one
        if (oldModeKey !== newModeKey) {
            delete updatedModes[oldModeKey];

            // If we're renaming the currently selected mode, update the selection
            if (mode === oldModeKey) {
                setSettingsObj({ mode: newModeKey });
                // Write the mode change to the database immediately
                if (userObj.user) {
                    writeSettings(userObj.user, { mode: newModeKey }, environmentObj.environment);
                }
            }
        }

        // Set the mode with new/updated key
        updatedModes[newModeKey] = updatedMode;

        onCustomModesChange(updatedModes);
        setEditingMode(null);
    };

    const handlePremadeModeSelect = (premadeMode: PremadeMode) => {
        setSelectedPremadeMode(premadeMode);
        setIsCreating(true);
    };

    const handleCreateCancel = () => {
        setIsCreating(false);
        setSelectedPremadeMode(null);
    };

    return (
        <div className="flex flex-col space-y-4 w-full">
            <div className="flex items-center space-x-1">
                <span className="text-sm dark:text-neutral-300">Custom Modes</span>
                <InfoButton
                    tooltipId="custom-modes-tooltip"
                    tooltipContent="Create custom modes that will appear in the mode selector. You can define their names, descriptions, instructions, and preferred AI model."
                    direction="bottom"
                />
            </div>

            <div className="space-y-2">
                {Object.entries(customModes).map(([modeKey, mode]) => {
                    const customMode = mode as CustomMode;
                    return (
                        <div key={modeKey}>
                            {editingMode === modeKey ? (
                                <EditableMode
                                    mode={customMode}
                                    modeName={modeKey}
                                    modeKey={modeKey}
                                    isNameDuplicate={isNameDuplicate}
                                    onSave={handleEditMode}
                                    onCancel={() => setEditingMode(null)}
                                />
                            ) : (
                                <ModeListItem
                                    mode={customMode}
                                    modeName={modeKey}
                                    onEdit={() => setEditingMode(modeKey)}
                                    onDelete={() => setDeletingMode(modeKey)}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {isCreating ? (
                <CreateModeForm
                    isNameDuplicate={isNameDuplicate}
                    onCreate={handleCreateMode}
                    onCancel={handleCreateCancel}
                    initialMode={selectedPremadeMode || undefined}
                />
            ) : (
                <div className="space-y-8">
                    <PremadeModeSuggestions
                        existingModes={customModes}
                        onSelectPremadeMode={handlePremadeModeSelect}
                    />
                    <div className="flex justify-center">
                        <button
                            onClick={() => setIsCreating(true)}
                            className="flex items-center space-x-2 bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-2.5 rounded-md text-sm"
                        >
                            <MdAdd size={16} />
                            <span>Add Custom Mode</span>
                        </button>
                    </div>
                </div>
            )}

            <DeleteConfirmationDialog
                isVisible={!!deletingMode}
                onDelete={() => deletingMode && handleDeleteMode(deletingMode)}
                onCancel={() => setDeletingMode(null)}
            />
        </div>
    );
};

export default CustomModesSection;